/**
 * Created by neo on 18.01.17.
 */
import { makeAutoObservable } from 'mobx';
import { Media } from '../Media/Media';
import { ExerciseVariation } from '../ProgramPortfolio/ExerciseVariation';
import { v4 as UUID } from 'uuid';
import { CoachExerciseBlock } from '../Coach/Program/Workout/CoachExerciseBlock';
import { CoachExerciseBlockSet, createCoachExerciseBlockSet } from '../Coach/Program/Workout/CoachExerciseBlockSet';
import { BaseTrackingKey } from '../ProgramPortfolio/TrackingKeys';
import { logger } from '../../Utils/logger';
import { ExerciseBlockLog } from './ExerciseBlockLog';
import type { ViewPhase } from './ViewPhase';
import { ViewSet, createViewSet } from './ViewSet';
import type { ExerciseSet } from './ExerciseSet';
import __DEV__ from '../../Utils/Dev';
import type { WorkoutLog } from './WorkoutLog';
import { CoachWorkout } from '../Coach/Program/Workout/CoachWorkout';

export type ViewBlock = {
  id: string;
  logBlock?: ExerciseBlockLog;
  viewPhase: ViewPhase;
  block: CoachExerciseBlock;
  sets: ViewSet[];
  readonly workoutLog: WorkoutLog | undefined;
  readonly viewBlockId: string;
  readonly firstSet: ViewSet | undefined;
  readonly index: number;
  readonly nextIndexOrCurrent: number;
  readonly workout: CoachWorkout | undefined;
  readonly workoutId: string | undefined;
  readonly setCount: number;
  readonly isWorkoutActive: boolean;
  readonly exercise: ExerciseVariation;
  readonly description: string;
  readonly currentSet: ExerciseSet | undefined;
  readonly currentViewSet: ViewSet | undefined;
  readonly nextSetOrLast: ViewSet | undefined;
  readonly nextSet: ViewSet;
  readonly currentNonCompletedViewSet: ViewSet | undefined;
  readonly completedSetsCount: number;
  readonly plannedSetsCount: number;
  readonly completed: boolean;
  readonly completedProgress: number;
  readonly lastSet: ViewSet | undefined;
  readonly isLastSet: boolean;
  readonly hasCompletedSets: boolean;
  readonly setTemplate: any;
  readonly videoMedia: Media | undefined;
  readonly previewVideoMedia: Media | undefined;
  readonly exerciseMedia: Media | undefined;
  readonly spotClassMedia: Media | undefined;
  readonly started: boolean;
  readonly phaseId: string | undefined;
  readonly trackingParameters: BaseTrackingKey[];
  readonly prevSet: ViewSet | undefined;
  readonly followingSet: ViewSet | undefined;
  readonly viewBlockIndex: number;
  readonly indexInWorkout: number;
  readonly isLastViewBlock: boolean;
  readonly prevViewBlock: ViewBlock | undefined;
  readonly nextViewBlock: ViewBlock | undefined;
  registerInViewPhase(): void;
  start(startDateTime?: Date, save?: boolean): ViewSet | undefined;
  startSet(viewSet: ViewSet, startDateTime?: Date, save?: boolean): ViewSet | undefined;
  prepareNextViewSet(): ViewSet;
  prepareViewSet(viewSet: ViewSet): ViewSet;
  prepareSet(): ExerciseSet | undefined;
  removeSet(index: number): void;
  accepts(logBlock: ExerciseBlockLog): boolean;
  replay(): void;
  replace(newBlock: CoachExerciseBlock): Promise<ViewBlock>;
};

export function createViewBlock(viewPhase: ViewPhase, block: CoachExerciseBlock, logBlock?: ExerciseBlockLog): ViewBlock {
  const result: ViewBlock = makeAutoObservable({
    id: UUID(),
    logBlock,
    viewPhase,
    block,
    sets: [] as ViewSet[],

    registerInViewPhase() {
      if (!this.viewPhase.exerciseBlocks.find((b) => b.block.exerciseBlockId === this.block.exerciseBlockId)) {
        this.viewPhase.exerciseBlocks.push(this);
      }
    },

    start(startDateTime: Date = new Date(), save: boolean = true): ViewSet | undefined {
      if (this.workoutLog) this.workoutLog.stopExercise(false);
      return this.startSet(this.nextSet, startDateTime, save);
    },

    startSet(viewSet: ViewSet, startDateTime: Date = new Date(), save: boolean = true): ViewSet | undefined {
      const nextSet = this.prepareViewSet(viewSet);
      if (nextSet.logSet) {
        this.registerInViewPhase();
        nextSet.start(startDateTime, save);
        return nextSet;
      }
      return undefined;
    },

    prepareNextViewSet(): ViewSet {
      return this.prepareViewSet(this.nextSet);
    },

    prepareViewSet(viewSet: ViewSet): ViewSet {
      viewSet.logSet = viewSet.logSet ?? this.prepareSet();
      if (viewSet.logSet) {
        if (!this.sets.find((s) => s === viewSet)) this.sets.push(viewSet);
      }
      return viewSet;
    },

    prepareSet(): ExerciseSet | undefined {
      if (this.workoutLog && !this.workoutLog.ended) {
        if (!this.logBlock) {
          this.logBlock = this.workoutLog.logExercise(this.exercise, this.phaseId, this.block.exerciseBlockId);
          logger('logBlock created', this.logBlock?.exercise.name);
        } else if (__DEV__) {
          logger('logBlock exists already');
        }
        return this.logBlock?.addSet(this.setTemplate);
      }
      return undefined;
    },

    removeSet(index: number) {
      if (!this.isWorkoutActive && this.block) {
        this.block.removeSet(index);
        this.sets.splice(index, 1);
      }
    },

    accepts(logBlock: ExerciseBlockLog): boolean {
      if (!this.logBlock &&
        ((logBlock.exerciseBlockId && this.block.exerciseBlockId === logBlock.exerciseBlockId) ||
          (!logBlock.exerciseBlockId && this.block.exercise.id === logBlock.exercise.id))) {
        this.logBlock = logBlock;
        return true;
      }
      return false;
    },

    replay() {
      const { logBlock, block, isWorkoutActive } = this;
      if (logBlock && isWorkoutActive) {
        const logLength = logBlock.sets.length;
        const setsLength = block.sets.length;
        if (logLength >= setsLength) {
          this.sets = logBlock.sets.map((logSet, index) =>
            createViewSet(this, index < setsLength ? this.block.sets[index] : undefined, logSet)
          );
        } else {
          this.sets = block.sets.map((set, index) =>
            createViewSet(this, set, index < logLength ? logBlock.sets[index] : undefined)
          );
        }
      } else {
        this.sets = block.sets.map((set: CoachExerciseBlockSet) => createViewSet(this, set));
      }
    },

    replace(newBlock: CoachExerciseBlock): Promise<ViewBlock> {
      return this.block.replace(newBlock).then(() => this.replay()).then(() => this);
    },

    get workoutLog(): WorkoutLog | undefined {
      return this.viewPhase?.viewWorkout.workoutLog;
    },

    get viewBlockId(): string {
      const viewBlockId = this.viewPhase ? this.viewPhase.viewPhaseId : '';
      if (this.logBlock) return `${viewBlockId}_${this.logBlock.exerciseBlockId || this.logBlock.blockIndex}`;
      if (this.block) return `${viewBlockId}_${this.block.exerciseBlockId}`;
      return viewBlockId;
    },

    get firstSet(): ViewSet | undefined {
      return this.sets.length > 0 ? this.sets[0] : undefined;
    },

    get index(): number {
      return this.currentViewSet?.index ?? -1;
    },

    get nextIndexOrCurrent(): number {
      if (this.index !== -1) {
        if (!this.sets[this.index].isFinished) return this.index;
        const nextIndex = this.index + 1;
        return nextIndex < this.sets.length ? nextIndex : this.index;
      }
      return this.index;
    },

    get workout(): CoachWorkout | undefined {
      return this.viewPhase?.viewWorkout.workout;
    },

    get workoutId(): string | undefined {
      return this.workout?.id;
    },

    get setCount(): number {
      return this.sets.length;
    },

    get isWorkoutActive(): boolean {
      return this.viewPhase?.viewWorkout.active ?? false;
    },

    get exercise(): ExerciseVariation {
      return this.block.exercise;
    },

    get description(): string {
      return this.block.translatedDescription ?? '';
    },

    get currentSet(): ExerciseSet | undefined {
      return this.logBlock?.lastSet;
    },

    get currentViewSet(): ViewSet | undefined {
      const existing = this.currentSet ? this.sets.find((v) => v.logSet?.setId === this.currentSet?.setId) : undefined;
      return existing || (this.sets.length > 0 ? this.sets[0] : undefined);
    },

    get nextSetOrLast(): ViewSet | undefined {
      if (this.currentViewSet && !this.currentViewSet.isFinished) return this.currentViewSet;
      if (this.currentViewSet?.isFinished && this.currentViewSet.nextSet?.viewBlock.block.exerciseBlockId === this.block.exerciseBlockId) {
        return this.currentViewSet.nextSet;
      }
      return this.sets[this.sets.length - 1];
    },

    get nextSet(): ViewSet {
      if (this.currentViewSet && !this.currentViewSet.isStarted) return this.currentViewSet;
      if (this.currentViewSet?.isStarted && this.currentViewSet.nextSet) return this.currentViewSet.nextSet;
      return createViewSet(this, createCoachExerciseBlockSet(undefined, this.setTemplate), undefined);
    },

    get currentNonCompletedViewSet(): ViewSet | undefined {
      if (this.currentViewSet?.isFinished) return this.currentViewSet.nextSet;
      return this.currentViewSet;
    },

    get completedSetsCount(): number {
      return this.logBlock?.completedSetsCount ?? 0;
    },

    get plannedSetsCount(): number {
      return this.sets.length ?? 0;
    },

    get completed(): boolean {
      return this.completedSetsCount > 0 && this.completedSetsCount >= this.plannedSetsCount;
    },

    get completedProgress(): number {
      if (this.plannedSetsCount > 0) return this.completedSetsCount / this.plannedSetsCount;
      return 0;
    },

    get lastSet(): ViewSet | undefined {
      return this.sets.length > 0 ? this.sets[this.sets.length - 1] : undefined;
    },

    get isLastSet(): boolean {
      return this.logBlock ? this.logBlock.sets.length >= this.block.sets.length : false;
    },

    get hasCompletedSets(): boolean {
      if (this.logBlock) {
        for (const set of this.logBlock.sets) {
          if (set.isFinished) return true;
        }
      }
      return false;
    },

    get setTemplate(): any {
      if (this.currentViewSet || this.currentNonCompletedViewSet) {
        const viewSet = (this.currentNonCompletedViewSet || this.currentViewSet) as ViewSet;
        return { values: viewSet.values.toJSON() };
      }
      return { values: { MIN_DURATION: 60, MAX_DURATION: 90, MIN_BREAK: 30, MAX_BREAK: 120 } };
    },

    get videoMedia(): Media | undefined {
      return this.exercise.videoMedia;
    },

    get previewVideoMedia(): Media | undefined {
      return this.exercise.previewVideoMedia;
    },

    get exerciseMedia(): Media | undefined {
      return this.exercise.imageMedia;
    },

    get spotClassMedia(): Media | undefined {
      return this.exercise.imageMedia;
    },

    get started(): boolean {
      return !!this.logBlock && this.logBlock.started;
    },

    get phaseId(): string | undefined {
      return this.viewPhase?.phase ? this.viewPhase.phase.phaseId : undefined;
    },

    get trackingParameters(): BaseTrackingKey[] {
      return this.block ? this.block.trackingParameters : this.logBlock ? this.logBlock.exercise.trackingParameters : [];
    },

    get prevSet(): ViewSet | undefined {
      return this.lastSet || this.prevViewBlock?.prevSet;
    },

    get followingSet(): ViewSet | undefined {
      return this.firstSet || this.nextViewBlock?.followingSet;
    },

    get viewBlockIndex(): number {
      return this.viewPhase.exerciseBlocks.findIndex((b) => b === this);
    },

    get indexInWorkout(): number {
      return this.viewPhase.viewWorkout.exerciseBlocks.findIndex((b) => b === this);
    },

    get isLastViewBlock(): boolean {
      return this.viewPhase ? this.viewBlockIndex + 1 === this.viewPhase.exerciseBlocks.length : true;
    },

    get prevViewBlock(): ViewBlock | undefined {
      if (this.viewPhase) {
        if (this.viewBlockIndex === 0) return this.viewPhase.prevViewPhase?.prevBlock;
        return this.viewPhase.exerciseBlocks[this.viewBlockIndex - 1];
      }
      return undefined;
    },

    get nextViewBlock(): ViewBlock | undefined {
      if (this.viewPhase) {
        if (this.isLastViewBlock) return this.viewPhase.nextViewPhase?.nextBlock;
        const nextIndex = this.viewBlockIndex + 1;
        return nextIndex < this.viewPhase.exerciseBlocks.length ? this.viewPhase.exerciseBlocks[nextIndex] : undefined;
      }
      return undefined;
    },
  });

  return result;
}
